import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Upload, Plus, Edit, Trash2, Search, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function FoodBank({ user, onLogout }) {
  const navigate = useNavigate();
  const [foodItems, setFoodItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [showPromptDialog, setShowPromptDialog] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editFormData, setEditFormData] = useState({
    name: '',
    category: '',
    primary_unit: '',
    primary_unit_grams: '',
    countable: false,
    increment_type: 'whole',
    kcal_per_100g: '',
    protein_per_100g: '',
    carbs_per_100g: '',
    fat_per_100g: ''
  });
  const [copiedCategory, setCopiedCategory] = useState(null);

  // Helper: Normalize category name (lowercase, singular form)
  const normalizeCategory = (category) => {
    if (!category) return '';
    const lower = category.toLowerCase();
    // Convert common plurals to singular
    if (lower === 'grains') return 'grain';
    if (lower === 'proteins') return 'protein';
    if (lower === 'vegetables') return 'vegetable';
    if (lower === 'fruits') return 'fruit';
    if (lower === 'condiments') return 'condiment';
    if (lower === 'legumes') return 'legume';
    return lower;
  };

  // Helper: Get unique categories from food items (normalized, sorted)
  const getUniqueCategories = () => {
    return Array.from(new Set(foodItems.map(item => normalizeCategory(item.category)).filter(Boolean))).sort();
  };
  const csvPrompt = `Create a comprehensive food items database CSV with 200-300 common foods used in nutrition coaching.

CSV Format:
name,unit_display,grams_per_unit,kcal_per_100g,protein_per_100g,carbs_per_100g,fat_per_100g

Requirements:
- Include variety: proteins (chicken, fish, beef, eggs), carbs (rice, pasta, bread), vegetables, fruits, nuts, oils, dairy
- Use common units: piece, cup, tbsp, oz, slice, medium, large, small
- Accurate nutritional values per 100g
- Include both whole foods and common prepared items
- Mix of healthy and indulgent options

Example rows:
Chicken Breast,piece,150,165,31,0,3.6
Brown Rice,cup,195,370,7.9,77.2,2.9
Olive Oil,tbsp,13.5,884,0,0,100
Greek Yogurt,cup,245,97,10.2,3.6,5

Please generate a complete CSV file with 200-300 items following this format.`;
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    unit_display: '',
    grams_per_unit: '',
    kcal_per_100g: '',
    protein_per_100g: '',
    carbs_per_100g: '',
    fat_per_100g: ''
  });

  useEffect(() => {
    fetchFoodItems();
  }, []);

  useEffect(() => {
    let filtered = foodItems;
    
    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(item =>
        item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (item.aliases && item.aliases.some(alias => alias.toLowerCase().includes(searchTerm.toLowerCase())))
      );
    }
    
    // Apply category filter (normalized)
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(item => 
        normalizeCategory(item.category) === categoryFilter
      );
    }
    
    setFilteredItems(filtered);
  }, [searchTerm, categoryFilter, foodItems]);

  const fetchFoodItems = async () => {
    try {
      console.log('Fetching Food Bank from:', `${API}/food-bank`);
      const response = await axios.get(`${API}/food-bank`, {
        params: { limit: 2000, _t: Date.now() }  // Increased limit to show all items
      });
      console.log('Food Bank response:', response.data);
      
      // Check if we have foods array
      if (!response.data.foods || !Array.isArray(response.data.foods)) {
        console.error('Invalid response format:', response.data);
        toast.error('Invalid Food Bank data format');
        return;
      }
      
      // Transform Food Bank format to display format
      const transformedItems = response.data.foods.map(food => ({
        id: food.id,
        name: food.name,
        category: food.category,
        unit_display: food.primary_unit,
        grams_per_unit: food.primary_unit_grams || food.unit_weight_g || 100,
        kcal_per_100g: food.per_100g?.kcal || 0,
        protein_per_100g: food.per_100g?.protein_g || 0,
        carbs_per_100g: food.per_100g?.carbs_g || 0,
        fat_per_100g: food.per_100g?.fat_g || 0,
        countable: food.countable,
        increment_type: food.increment_type || 'whole',
        aliases: food.aliases,
        role: food.role,
        allergens: food.allergens,
        categories: food.categories
      }));
      
      console.log(`Transformed ${transformedItems.length} food items`);
      setFoodItems(transformedItems);
      setFilteredItems(transformedItems);
      toast.success(`Loaded ${transformedItems.length} foods from Food Bank`);
      
      // Also fetch expansion packs count
      fetchExpansionCount();
    } catch (error) {
      console.error('Food Bank error:', error);
      console.error('Error details:', error.response?.data);
      toast.error(`Failed to load Food Bank: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchExpansionCount = async () => {
    try {
      const token = localStorage.getItem('coach_token');
      const response = await fetch(`${API}/food-bank/packs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        // Sum up all new_items from packs
        const totalNew = data.packs?.reduce((sum, pack) => sum + (pack.new_items || 0), 0) || 0;
        // totalNew is available here for future use if needed
      }
    } catch (error) {
      console.error('Error fetching expansion count:', error);
    }
  };

  const handleAddItem = async (e) => {
    e.preventDefault();
    
    try {
      // Normalize category to lowercase for consistency
      const normalizedCategory = formData.category.toLowerCase().trim();
      
      await axios.post(`${API}/food-bank/bulk-add`, {
        items: [{
          name: formData.name,
          category: normalizedCategory,
          primary_unit: formData.unit_display,
          primary_unit_grams: parseFloat(formData.grams_per_unit),
          per_100g: {
            kcal: parseFloat(formData.kcal_per_100g),
            protein_g: parseFloat(formData.protein_per_100g),
            carbs_g: parseFloat(formData.carbs_per_100g),
            fat_g: parseFloat(formData.fat_per_100g)
          }
        }]
      });
      
      toast.success('Food item added successfully');
      setAddDialogOpen(false);
      setFormData({
        name: '',
        category: '',
        unit_display: '',
        grams_per_unit: '',
        kcal_per_100g: '',
        protein_per_100g: '',
        carbs_per_100g: '',
        fat_per_100g: ''
      });
      fetchFoodItems();
    } catch (error) {
      console.error('Add item error:', error);
      toast.error(error.response?.data?.detail || 'Failed to add food item');
    }
  };

  const handleImportCSV = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setImporting(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/import/food-bank`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      const stats = response.data.stats;
      const summary = response.data.summary;
      
      toast.success(summary);
      console.log('Import stats:', stats);
      
      fetchFoodItems();
    } catch (error) {
      console.error('Import error:', error);
      toast.error(error.response?.data?.detail || 'Failed to import CSV');
    } finally {
      setImporting(false);
      e.target.value = '';
    }
  };

  const exportCSV = () => {
    const csv = [
      ['name', 'unit_display', 'grams_per_unit', 'kcal_per_100g', 'protein_per_100g', 'carbs_per_100g', 'fat_per_100g'],
      ...foodItems.map(item => [
        item.name,
        item.unit_display,
        item.grams_per_unit,
        item.kcal_per_100g,
        item.protein_per_100g,
        item.carbs_per_100g,
        item.fat_per_100g
      ])
    ].map(row => row.join(',')).join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `food-bank-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    toast.success('Food bank exported');
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedItems([]);
    } else {
      setSelectedItems(filteredItems.map(item => item.id));
    }
    setSelectAll(!selectAll);
  };

  const handleSelectItem = (itemId) => {
    setSelectedItems(prev => {
      if (prev.includes(itemId)) {
        return prev.filter(id => id !== itemId);
      } else {
        return [...prev, itemId];
      }
    });
  };

  const copyItemsToClipboard = (category = 'all') => {
    let itemsToCopy = foodItems;
    
    // Filter by normalized category if specified
    if (category !== 'all') {
      itemsToCopy = foodItems.filter(item => normalizeCategory(item.category) === category);
    }
    
    // Format items for ChatGPT (clean, readable list with macros)
    const formattedText = itemsToCopy
      .map(item => {
        const unit = item.primary_unit || item.unit_display || 'g';
        const grams = item.primary_unit_grams || item.grams_per_unit || 100;
        const kcal = item.per_100g?.kcal || item.kcal_per_100g || 0;
        const protein = item.per_100g?.protein_g || item.protein_per_100g || 0;
        const carbs = item.per_100g?.carbs_g || item.carbs_per_100g || 0;
        const fat = item.per_100g?.fat_g || item.fat_per_100g || 0;
        
        return `${item.name} (${unit}, ${grams}g) - ${kcal} kcal/100g | P:${protein}g C:${carbs}g F:${fat}g`;
      })
      .join('\n');
    
    // Copy to clipboard
    navigator.clipboard.writeText(formattedText).then(() => {
      setCopiedCategory(category);
      toast.success(`Copied ${itemsToCopy.length} ${category === 'all' ? 'items' : category + ' items'}!`);
      setTimeout(() => setCopiedCategory(null), 2000);
    }).catch((err) => {
      // Fallback for older browsers or security restrictions
      const textArea = document.createElement('textarea');
      textArea.value = formattedText;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setCopiedCategory(category);
        toast.success(`Copied ${itemsToCopy.length} ${category === 'all' ? 'items' : category + ' items'}!`);
        setTimeout(() => setCopiedCategory(null), 2000);
      } catch (err) {
        toast.error('Failed to copy - please try manually');
      }
      document.body.removeChild(textArea);
    });
  };

  const handleBulkDelete = async () => {
    if (selectedItems.length === 0) {
      toast.error('No items selected');
      return;
    }

    if (!window.confirm(`Delete ${selectedItems.length} selected item(s)?`)) {
      return;
    }

    try {
      await Promise.all(selectedItems.map(id => 
        axios.delete(`${API}/food-bank/${id}`)
      ));
      toast.success(`Deleted ${selectedItems.length} items`);
      setSelectedItems([]);
      setSelectAll(false);
      fetchFoodItems();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete items');
    }
  };

  // Auto-fill gram weights based on unit and food type
  const getDefaultGramsForUnit = (unit, foodName = '') => {
    const nameLower = foodName.toLowerCase();
    
    // Check food type
    const isOil = nameLower.includes('oil') || nameLower.includes('butter') || nameLower.includes('ghee');
    const isLiquid = nameLower.includes('milk') || nameLower.includes('water') || nameLower.includes('juice') || 
                     nameLower.includes('cream') || nameLower.includes('yogurt');
    const isDryIngredient = nameLower.includes('flour') || nameLower.includes('sugar') || nameLower.includes('salt') ||
                           nameLower.includes('oats') || nameLower.includes('rice');
    const isNutButter = nameLower.includes('peanut butter') || nameLower.includes('almond butter');
    
    switch(unit) {
      case 'tbsp':
        if (isOil) return 13.5;
        if (isNutButter) return 16;
        if (isDryIngredient) return 12;
        return 15; // Default
      
      case 'tsp':
        if (isOil) return 4.5;
        if (isDryIngredient) return 4;
        return 5; // Default
      
      case 'cup':
        if (isLiquid) return 240;
        if (isDryIngredient) return 120; // Dry ingredients are lighter
        return 200; // Default
      
      case 'oz':
        return 28;
      
      case 'ml':
        return 1; // 1ml = 1g for water-based liquids
      
      case 'g':
        return 1;
      
      case 'piece':
      case 'whole':
        // Common defaults
        if (nameLower.includes('egg')) return 50;
        if (nameLower.includes('tomato')) return 120;
        if (nameLower.includes('apple') || nameLower.includes('orange')) return 180;
        if (nameLower.includes('potato')) return 150;
        if (nameLower.includes('avocado')) return 200;
        return 100; // Default
      
      case 'slice':
        return 30;
      
      case 'handful':
        return 30;
      
      case 'scoop':
        return 30;
      
      default:
        return 100;
    }
  };

  const handleUnitChange = (newUnit) => {
    const newGrams = getDefaultGramsForUnit(newUnit, editFormData.name);
    setEditFormData({
      ...editFormData, 
      primary_unit: newUnit,
      primary_unit_grams: newGrams
    });
  };

  const handleEditClick = (item) => {
    setEditingItem(item);
    setEditFormData({
      name: item.name,
      category: item.category,
      primary_unit: item.unit_display,
      primary_unit_grams: item.grams_per_unit,
      countable: item.countable || false,
      increment_type: item.increment_type || 'whole',
      kcal_per_100g: item.kcal_per_100g,
      protein_per_100g: item.protein_per_100g,
      carbs_per_100g: item.carbs_per_100g,
      fat_per_100g: item.fat_per_100g
    });
    setEditDialogOpen(true);
  };

  const handleIncrementChange = (value) => {
    console.log('Increment changed to:', value);
    setEditFormData(prev => {
      // Automatically set countable based on increment_type
      // "whole" = countable (eggs, bananas, apples)
      // Others = not countable (fractional amounts allowed)
      const countable = value === 'whole';
      const updated = {...prev, increment_type: value, countable: countable};
      console.log('Updated form data:', updated, '- countable:', countable);
      return updated;
    });
  };

  const handleEditSave = async () => {
    // Validate data
    if (!editFormData.name || !editFormData.category || !editFormData.primary_unit) {
      toast.error('Please fill in all required fields');
      return;
    }

    const gramsPerUnit = parseFloat(editFormData.primary_unit_grams);
    const kcal = parseFloat(editFormData.kcal_per_100g);
    const protein = parseFloat(editFormData.protein_per_100g);
    const carbs = parseFloat(editFormData.carbs_per_100g);
    const fat = parseFloat(editFormData.fat_per_100g);

    if (isNaN(gramsPerUnit) || gramsPerUnit <= 0) {
      toast.error('Grams per unit must be a positive number');
      return;
    }

    if (isNaN(kcal) || isNaN(protein) || isNaN(carbs) || isNaN(fat)) {
      toast.error('All macro values must be valid numbers');
      return;
    }

    console.log('Saving increment_type:', editFormData.increment_type);

    try {
      // Normalize category to lowercase for consistency
      const normalizedCategory = editFormData.category.toLowerCase().trim();
      
      const response = await axios.put(`${API}/food-bank/${editingItem.id}`, {
        name: editFormData.name,
        category: normalizedCategory,
        primary_unit: editFormData.primary_unit,
        primary_unit_grams: gramsPerUnit,
        countable: editFormData.countable,
        increment_type: editFormData.increment_type,
        kcal_per_100g: kcal,
        protein_per_100g: protein,
        carbs_per_100g: carbs,
        fat_per_100g: fat
      });
      
      toast.success(`✅ Updated ${editFormData.name} - Display: ${editFormData.increment_type}`);
      setEditDialogOpen(false);
      setEditingItem(null);
      fetchFoodItems();
    } catch (error) {
      console.error('Edit error:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to update food item';
      toast.error(errorMsg);
    }
  };

  if (loading) {
    return <div data-testid="loading">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50">
      <Navbar user={user} onLogout={onLogout} />
      
      <div className="page-container">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2" data-testid="food-bank-title">
              Food Bank
            </h1>
            <p className="text-lg text-gray-600">
              {foodItems.length} verified foods with accurate macros • Browse and search
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button 
              variant="outline"
              onClick={() => document.getElementById('csv-upload-input').click()}
              disabled={importing}
            >
              <Upload className="h-4 w-4 mr-2" />
              {importing ? 'Importing...' : 'Import CSV'}
            </Button>
            <input
              id="csv-upload-input"
              type="file"
              accept=".csv"
              onChange={handleImportCSV}
              style={{ display: 'none' }}
            />
            <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-gradient-to-r from-purple-600 to-pink-600">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Item
                </Button>
              </DialogTrigger>
              <DialogTrigger asChild>
                <Button variant="outline" onClick={(e) => {
                  e.preventDefault();
                  navigate('/coach/food-bank/manage');
                }}>
                  <Edit className="h-4 w-4 mr-2" />
                  Manage Items
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add Food Item</DialogTitle>
                  <DialogDescription>Add a new food item to your bank</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleAddItem} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="category">Category *</Label>
                    <Input
                      id="category"
                      list="category-suggestions-add"
                      value={formData.category}
                      onChange={(e) => setFormData({...formData, category: e.target.value})}
                      placeholder="Type or select a category"
                      required
                    />
                    <datalist id="category-suggestions-add">
                      {getUniqueCategories().map(cat => (
                        <option key={cat} value={cat.charAt(0).toUpperCase() + cat.slice(1)} />
                      ))}
                    </datalist>
                    <p className="text-xs text-gray-500">
                      Select existing or create new category
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="unit_display">Unit *</Label>
                      <Input
                        id="unit_display"
                        placeholder="piece, cup, tbsp"
                        value={formData.unit_display}
                        onChange={(e) => setFormData({...formData, unit_display: e.target.value})}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="grams_per_unit">Grams/Unit *</Label>
                      <Input
                        id="grams_per_unit"
                        type="number"
                        step="0.1"
                        value={formData.grams_per_unit}
                        onChange={(e) => setFormData({...formData, grams_per_unit: e.target.value})}
                        required
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="kcal_per_100g">kcal/100g *</Label>
                      <Input
                        id="kcal_per_100g"
                        type="number"
                        step="0.1"
                        value={formData.kcal_per_100g}
                        onChange={(e) => setFormData({...formData, kcal_per_100g: e.target.value})}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="protein_per_100g">Protein/100g *</Label>
                      <Input
                        id="protein_per_100g"
                        type="number"
                        step="0.1"
                        value={formData.protein_per_100g}
                        onChange={(e) => setFormData({...formData, protein_per_100g: e.target.value})}
                        required
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="carbs_per_100g">Carbs/100g *</Label>
                      <Input
                        id="carbs_per_100g"
                        type="number"
                        step="0.1"
                        value={formData.carbs_per_100g}
                        onChange={(e) => setFormData({...formData, carbs_per_100g: e.target.value})}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="fat_per_100g">Fat/100g *</Label>
                      <Input
                        id="fat_per_100g"
                        type="number"
                        step="0.1"
                        value={formData.fat_per_100g}
                        onChange={(e) => setFormData({...formData, fat_per_100g: e.target.value})}
                        required
                      />
                    </div>
                  </div>
                  <Button type="submit" className="w-full">
                    Add Food Item
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        <Card>
          <CardHeader>
            <div className="flex items-center gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                <Input
                  type="text"
                  placeholder="Search food items..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="all">All Categories ({foodItems.length})</option>
                {getUniqueCategories().map(category => {
                    // Count items matching this normalized category
                    const count = foodItems.filter(item => normalizeCategory(item.category) === category).length;
                    const label = category.charAt(0).toUpperCase() + category.slice(1);
                    return (
                      <option key={category} value={category}>
                        {label} ({count})
                      </option>
                    );
                  })}
              </select>
            </div>
            
            {/* Copy to Clipboard Section */}
            <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">Copy for ChatGPT</h3>
                  <p className="text-xs text-gray-600">Quick copy food items by category</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant={copiedCategory === 'all' ? 'default' : 'outline'}
                  onClick={() => copyItemsToClipboard('all')}
                  className="text-xs"
                >
                  {copiedCategory === 'all' ? (
                    <>
                      <Check className="h-3 w-3 mr-1" />
                      Copied All
                    </>
                  ) : (
                    <>
                      <Copy className="h-3 w-3 mr-1" />
                      All Items ({foodItems.length})
                    </>
                  )}
                </Button>
                
                {/* Dynamically generate category buttons from actual food items */}
                {getUniqueCategories().map(category => {
                    // Count items matching this normalized category
                    const count = foodItems.filter(item => normalizeCategory(item.category) === category).length;
                    const label = category.charAt(0).toUpperCase() + category.slice(1);
                  
                    return (
                      <Button
                        key={category}
                        size="sm"
                        variant={copiedCategory === category ? 'default' : 'outline'}
                        onClick={() => copyItemsToClipboard(category)}
                        className="text-xs"
                      >
                        {copiedCategory === category ? (
                          <>
                            <Check className="h-3 w-3 mr-1" />
                            Copied
                          </>
                        ) : (
                          <>
                            <Copy className="h-3 w-3 mr-1" />
                            {label} ({count})
                          </>
                        )}
                      </Button>
                    );
                  })}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {selectedItems.length > 0 && (
              <div className="mb-4 flex items-center gap-4">
                <Button 
                  variant="destructive" 
                  onClick={handleBulkDelete}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete {selectedItems.length} Selected
                </Button>
                <span className="text-sm text-gray-600">
                  {selectedItems.length} of {filteredItems.length} items selected
                </span>
              </div>
            )}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 w-12">
                      <input
                        type="checkbox"
                        checked={selectAll}
                        onChange={handleSelectAll}
                        className="w-4 h-4 cursor-pointer"
                      />
                    </th>
                    <th className="text-left py-3 px-4">Name</th>
                    <th className="text-left py-3 px-4">Category</th>
                    <th className="text-left py-3 px-4">Unit</th>
                    <th className="text-right py-3 px-4">g/Unit</th>
                    <th className="text-right py-3 px-4">kcal/100g</th>
                    <th className="text-right py-3 px-4">P/100g</th>
                    <th className="text-right py-3 px-4">C/100g</th>
                    <th className="text-right py-3 px-4">F/100g</th>
                    <th className="text-center py-3 px-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredItems.length === 0 ? (
                    <tr>
                      <td colSpan="10" className="text-center py-8 text-gray-500">
                        {searchTerm ? 'No items found' : 'No food items yet. Add some or import a CSV.'}
                      </td>
                    </tr>
                  ) : (
                    filteredItems.map((item) => (
                      <tr key={item.id} className="border-b hover:bg-gray-50">
                        <td className="py-3 px-4">
                          <input
                            type="checkbox"
                            checked={selectedItems.includes(item.id)}
                            onChange={() => handleSelectItem(item.id)}
                            className="w-4 h-4 cursor-pointer"
                          />
                        </td>
                        <td className="py-3 px-4 font-semibold">{item.name}</td>
                        <td className="py-3 px-4 text-sm text-gray-600">{item.category}</td>
                        <td className="py-3 px-4">{item.unit_display}</td>
                        <td className="py-3 px-4 text-right">{item.grams_per_unit}</td>
                        <td className="py-3 px-4 text-right">{item.kcal_per_100g}</td>
                        <td className="py-3 px-4 text-right">{item.protein_per_100g}g</td>
                        <td className="py-3 px-4 text-right">{item.carbs_per_100g}g</td>
                        <td className="py-3 px-4 text-right">{item.fat_per_100g}g</td>
                        <td className="py-3 px-4 text-center">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditClick(item)}
                            className="h-8 w-8 p-0"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        <Card className="mt-6">
          <CardHeader>
            <CardTitle>CSV Import Format</CardTitle>
            <CardDescription>Use this format when importing food items</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="bg-gray-50 p-4 rounded font-mono text-sm overflow-x-auto">
              <div>name,unit_display,grams_per_unit,kcal_per_100g,protein_per_100g,carbs_per_100g,fat_per_100g</div>
              <div>Chicken Breast,piece,150,165,31,0,3.6</div>
              <div>Brown Rice,cup,195,370,7.9,77.2,2.9</div>
              <div>Olive Oil,tbsp,13.5,884,0,0,100</div>
            </div>
          </CardContent>
        </Card>

        {/* Edit Food Item Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Food Item</DialogTitle>
              <DialogDescription>
                Update measurement units and macros. Changes apply to all future meal plans.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="edit-name">Food Name</Label>
                <Input
                  id="edit-name"
                  value={editFormData.name}
                  onChange={(e) => setEditFormData({...editFormData, name: e.target.value})}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-category">Category</Label>
                <Input
                  id="edit-category"
                  list="category-suggestions"
                  value={editFormData.category}
                  onChange={(e) => setEditFormData({...editFormData, category: e.target.value})}
                  placeholder="Type or select a category"
                />
                <datalist id="category-suggestions">
                  {getUniqueCategories().map(cat => (
                    <option key={cat} value={cat.charAt(0).toUpperCase() + cat.slice(1)} />
                  ))}
                </datalist>
                <p className="text-xs text-gray-500">
                  Select from existing categories or type a new one
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-unit">Measurement Unit</Label>
                  <Select value={editFormData.primary_unit} onValueChange={handleUnitChange}>
                    <SelectTrigger id="edit-unit">
                      <SelectValue placeholder="Select unit" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="g">g (grams)</SelectItem>
                      <SelectItem value="ml">ml (milliliters)</SelectItem>
                      <SelectItem value="tbsp">tbsp (tablespoon)</SelectItem>
                      <SelectItem value="tsp">tsp (teaspoon)</SelectItem>
                      <SelectItem value="cup">cup</SelectItem>
                      <SelectItem value="oz">oz (ounce)</SelectItem>
                      <SelectItem value="piece">piece</SelectItem>
                      <SelectItem value="whole">whole</SelectItem>
                      <SelectItem value="slice">slice</SelectItem>
                      <SelectItem value="handful">handful</SelectItem>
                      <SelectItem value="scoop">scoop</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500">Standardized units for consistency</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-grams">Grams per Unit</Label>
                  <Input
                    id="edit-grams"
                    type="number"
                    step="0.1"
                    placeholder="50"
                    value={editFormData.primary_unit_grams}
                    onChange={(e) => setEditFormData({...editFormData, primary_unit_grams: e.target.value})}
                  />
                  <p className="text-xs text-gray-500">e.g., 1 egg = 50g, 1 oz = 28.35g</p>
                </div>
              </div>

              <div className="space-y-3 border-t pt-4">
                <Label className="text-base font-semibold">Amount Display Format</Label>
                <p className="text-sm text-gray-600">Choose how this food item displays in recipes:</p>
                <div className="space-y-2">
                  <label 
                    className="flex items-center space-x-3 cursor-pointer p-3 hover:bg-gray-50 rounded border border-transparent hover:border-gray-200"
                    onClick={() => handleIncrementChange('whole')}
                  >
                    <input
                      type="radio"
                      name="increment_type"
                      value="whole"
                      checked={editFormData.increment_type === 'whole'}
                      onChange={() => handleIncrementChange('whole')}
                      className="w-5 h-5 cursor-pointer"
                    />
                    <span className="flex-1">
                      <span className="font-semibold">Whole numbers only</span> - 1, 2, 3 
                      <span className="text-gray-500 text-xs ml-2">(eggs, chicken pieces, bananas)</span>
                    </span>
                  </label>

                  <label 
                    className="flex items-center space-x-3 cursor-pointer p-3 hover:bg-gray-50 rounded border border-transparent hover:border-gray-200"
                    onClick={() => handleIncrementChange('half')}
                  >
                    <input
                      type="radio"
                      name="increment_type"
                      value="half"
                      checked={editFormData.increment_type === 'half'}
                      onChange={() => handleIncrementChange('half')}
                      className="w-5 h-5 cursor-pointer"
                    />
                    <span className="flex-1">
                      <span className="font-semibold">0.5 increments</span> - 0.5, 1, 1.5, 2 
                      <span className="text-gray-500 text-xs ml-2">(avocados, cups, tbsp)</span>
                    </span>
                  </label>

                  <label 
                    className="flex items-center space-x-3 cursor-pointer p-3 hover:bg-gray-50 rounded border border-transparent hover:border-gray-200"
                    onClick={() => handleIncrementChange('quarter')}
                  >
                    <input
                      type="radio"
                      name="increment_type"
                      value="quarter"
                      checked={editFormData.increment_type === 'quarter'}
                      onChange={() => handleIncrementChange('quarter')}
                      className="w-5 h-5 cursor-pointer"
                    />
                    <span className="flex-1">
                      <span className="font-semibold">0.25 increments</span> - 0.25, 0.5, 0.75, 1 
                      <span className="text-gray-500 text-xs ml-2">(precise measurements)</span>
                    </span>
                  </label>

                  <label 
                    className="flex items-center space-x-3 cursor-pointer p-3 hover:bg-gray-50 rounded border border-transparent hover:border-gray-200"
                    onClick={() => handleIncrementChange('decimal')}
                  >
                    <input
                      type="radio"
                      name="increment_type"
                      value="decimal"
                      checked={editFormData.increment_type === 'decimal'}
                      onChange={() => handleIncrementChange('decimal')}
                      className="w-5 h-5 cursor-pointer"
                    />
                    <span className="flex-1">
                      <span className="font-semibold">Any value</span> - 150, 155, 200 
                      <span className="text-gray-500 text-xs ml-2">(for grams/ml)</span>
                    </span>
                  </label>

                </div>
              </div>

              <div className="border-t pt-4">
                <h4 className="font-semibold mb-3">Macros per 100g</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit-kcal">Calories</Label>
                    <Input
                      id="edit-kcal"
                      type="number"
                      step="0.1"
                      value={editFormData.kcal_per_100g}
                      onChange={(e) => setEditFormData({...editFormData, kcal_per_100g: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-protein">Protein (g)</Label>
                    <Input
                      id="edit-protein"
                      type="number"
                      step="0.1"
                      value={editFormData.protein_per_100g}
                      onChange={(e) => setEditFormData({...editFormData, protein_per_100g: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-carbs">Carbs (g)</Label>
                    <Input
                      id="edit-carbs"
                      type="number"
                      step="0.1"
                      value={editFormData.carbs_per_100g}
                      onChange={(e) => setEditFormData({...editFormData, carbs_per_100g: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-fat">Fat (g)</Label>
                    <Input
                      id="edit-fat"
                      type="number"
                      step="0.1"
                      value={editFormData.fat_per_100g}
                      onChange={(e) => setEditFormData({...editFormData, fat_per_100g: e.target.value})}
                    />
                  </div>
                </div>
              </div>

              <div className="flex gap-2 pt-4">
                <Button onClick={handleEditSave} className="flex-1">
                  Save Changes
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setEditDialogOpen(false);
                    setEditingItem(null);
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
